<script setup lang="ts">
import {
  defineAsyncComponent, inject, onMounted, type Ref, ref,
} from 'vue';
import {
  CdrBreadcrumb, CdrText,
} from '@rei/cedar';

import GladlySearch from '../../../components/gladlySearchBar/GladlySearch.vue';
import LinkMembership from '../../../components/linkMembership/LinkMembership.vue';
import CategoriesCards from '../components/CategoriesCards.vue';
import CategoryDetails from '../components/CategoryDetails.vue';

import { type ICategoriesComponent, ICategoriesComponentKeys } from '../models/interfaces/CategoriesComponent';
import { camelCase } from '../../../common/utils';
import { useUserAccountData } from '../../../common/composables/useUserAccountData';
import { apiCall } from '../../../api/services/apiCalls';
import { HelpCenterSectionsModel } from '../../../api/types/HelpCenterSectionsModel';

const store: any = inject('store');
const props: ICategoriesComponent = store.getValues(ICategoriesComponentKeys);

const QuickToolsComponent = defineAsyncComponent(() => import('../../../components/quickTools/QuickTools.vue'));
const CatPopularLinksComponent = defineAsyncComponent(() => import('../components/CatPopularLinksComponent.vue'));
const HelpErrorPage = defineAsyncComponent(() => import('../components/HelpErrorPage.vue'));

const userInfo = ref({
  isLoggedIn: false,
  isMember: false,
});

const selectedCategoryDetails: Ref<{ [key: string]: any } | undefined> = ref({});
const hcSections = ref<HelpCenterSectionsModel[]>([]);
const isLoadingHcSections = ref(true);
const hasAttemptedLoad = ref(false);
const breadCrumbItems = ref([{
  item: {
    url: '/help',
    name: 'Help Center',
  },
}]);

const findSelectedHelpCenter = (arr: any[] | undefined) => arr && arr.find((el: { helpCenterName: any; }) => camelCase(el.helpCenterName) === props.helpCenterName);
const selectedHelpCenter = findSelectedHelpCenter(props.hasSecondaryHC ? props.gladlyNewHelpCenters : props.helpCenterNames);

const calculateSelectedCategoryDetails = () => {
  if (props.hasSecondaryHC && props.selectedCategory === 'seeAll') {
    const categoryDetails: { [key: string]: any } = {};
    const headerTemplate = `for ${selectedHelpCenter?.helpCenterName}`;
    categoryDetails.header = `All help content ${selectedHelpCenter
      ? headerTemplate : ''}`;
    categoryDetails.id = '';
    categoryDetails.answers = hcSections.value && hcSections.value.map(((section) => section.answers)).flat(1);
    selectedCategoryDetails.value = categoryDetails;
  } else {
    selectedCategoryDetails.value = hcSections.value && hcSections.value.find((hc) => {
      if (props.selectedCategory === 'seeAll' && hc.header) {
        return hc.header.includes('All help content');
      }
      return camelCase(hc.header) === props.selectedCategory;
    });
  }
};

const pushToBreadCrumbz = (url: string, name: string) => {
  breadCrumbItems.value.push({
    item: {
      url,
      name,
    },
  });
};

onMounted(async () => {
  userInfo.value = await useUserAccountData();

  if (props.hasSecondaryHC) {
    const getMainHCDetails = props.helpCenterNames.find((hc) => camelCase(hc.helpCenterName) === selectedHelpCenter?.mainHelpCenter);
    pushToBreadCrumbz(
      getMainHCDetails
        ? `/help/helpCenters?hc=${camelCase(getMainHCDetails?.helpCenterName) || ''}` : '',
      (getMainHCDetails?.helpCenterName) || '',
    );

    const currentUrl = window.location.href;
    if (!currentUrl.includes('categories')) {
      pushToBreadCrumbz(
        props.selectedCategory
          ? `/help/categories?hc=${camelCase(selectedHelpCenter?.mainHelpCenter) || ''}&secHc=${camelCase(selectedHelpCenter?.helpCenterName) || ''}`
          : '',
        (selectedHelpCenter?.helpCenterName) || '',
      );
    }
  } else {
    pushToBreadCrumbz(
      props.selectedCategory
        ? `/help/categories?hc=${camelCase(selectedHelpCenter?.helpCenterName) || ''}`
        : '',
      (selectedHelpCenter?.helpCenterName) || '',
    );
  }
});

(async () => {
  try {
    const helpCenterCategories = await apiCall(
      'default',
      `help-topics/helpCenterIds/${selectedHelpCenter.helpCenterId}`,
      'true',
      'includeDetails',
    );
    // SACS-3516: Defensive check to prevent destructuring of undefined.
    // If helpCenterCategories is an Error or empty array, sections will be undefined
    const sections = (Array.isArray(helpCenterCategories) && helpCenterCategories.length > 0)
      ? helpCenterCategories[0]?.sections
      : undefined;

    if ((sections && sections.length > 0) && props.selectedCategory) {
      // Temporarily set hcSections for calculateSelectedCategoryDetails to use
      hcSections.value = sections;
      calculateSelectedCategoryDetails();
    }

    if (sections && sections.length > 0) {
      const filteredSections = sections.filter((obj) => obj.header && !obj.header.includes('All help content'));
      const results: any[] = [];
      const auxSection = [...filteredSections];
      while (auxSection.length) {
        results.push(auxSection.splice(0, 3));
      }
      hcSections.value = results;
    } else {
      hcSections.value = [];
    }
  } catch (error) {
    console.error('Error loading help center sections:', error);
    hcSections.value = [];
  } finally {
    // Set these flags after all data processing and hcSections assignment is complete
    // This prevents flickering of HelpErrorPage during data transformation
    hasAttemptedLoad.value = true;
    isLoadingHcSections.value = false;
  }
})();
</script>

<template>
  <div
    id="help-center-category"
    class="help-center-category"
  >
    <div
      id="help-center-category-section-header"
      class="help-center-category-section-header"
    >
      <CdrBreadcrumb
        v-if="(hcSections && hcSections.length > 0)"
        :truncation-enabled="false"
        :items="breadCrumbItems"
        class="help-center-category-bc"
      />
      <CdrText
        tag="h1"
        tabindex="0"
        class="help-center-category-heading"
        data-ui="help-center-category-heading"
      >
        {{ (selectedHelpCenter && selectedHelpCenter.helpCenterName) || '' }}
      </CdrText>
    </div>
    <GladlySearch
      :parent="'Category'"
    />
    <LinkMembership
      v-if="userInfo?.isLoggedIn && !userInfo?.isMember && selectedHelpCenter?.helpCenterName === 'Membership & Rewards'"
    />
    <QuickToolsComponent
      v-if="props.showQuickToolsOnPage.showOnCategoryPage && (props.quickTools && props.quickTools.length)"
      :from="true"
      :adobe-tag="(selectedHelpCenter && selectedHelpCenter.helpCenterName) || ''"
    />
    <CatPopularLinksComponent
      v-if="(props.catPopularLinks && props.catPopularLinks.length > 0) && !Object.keys(selectedCategoryDetails).length"
      :selected-help-center="selectedHelpCenter"
    />
    <!-- Render CategoriesCards only when data are ready to be shown -->
    <CategoriesCards
      v-if="!isLoadingHcSections && hcSections && hcSections.length > 0"
      :selectedCategoryDetails="selectedCategoryDetails"
      :selectedHelpCenter="selectedHelpCenter"
      :hcSections="hcSections"
    />
    <HelpErrorPage v-if="!isLoadingHcSections && hasAttemptedLoad && (!hcSections || hcSections.length === 0)" />
  </div>

  <CategoryDetails
    v-if="!isLoadingHcSections"
    :selectedCategoryDetails="selectedCategoryDetails"
    :selectedHelpCenter="selectedHelpCenter"
    :hcSections="hcSections"
  />
</template>
